/**
 * useAuth — React Query hooks for all authentication operations.
 *
 *  useLogin()          — POST /auth/login
 *  useForgotPassword() — POST /auth/forgot-password
 *  useResetPassword()  — POST /auth/reset-password
 *  useGetMe()           — GET  /auth/me  (auto-fires when token is present)
 *  useLogout()          — POST /auth/logout
 *  useChangePassword()  — POST /auth/change-password
 *
 * Navigation and toast feedback live here so the pages/components
 * that consume these hooks stay thin.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  loginApi,
  forgotPasswordApi,
  resetPasswordApi,
  getMeApi,
  logoutApi,
  changePasswordApi,
} from "../api/authApi";
import { useAuthStore } from "../store/authStore";
import { usePortalAuthStore } from "../store/portalAuthStore";
import { LoginFormType } from "../types/login";
import { AuthUserProps } from "../types/auth";
import type { ForgotPasswordFormType } from "../types/forgotPassword";
import type { ResetPasswordPayload } from "../types/resetPassword";
import type { ChangePasswordFormType } from "../types/changePassword";
import { AxiosError } from "axios";
import { endOpenVisit, telemetryQueue } from "src/telemetry";

// ─── Query key factory ────────────────────────────────────────────────────────

export const AUTH_QUERY_KEYS = {
  me: ["auth", "me"] as const,
} as const;

// ─── useLogin ─────────────────────────────────────────────────────────────────

export function useLogin() {
  const navigate = useNavigate();
  const { loginSuccess } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginFormType) => loginApi(credentials),

    onSuccess: (data) => {
      // Store tokens in cookies via loginSuccess (expiry set from API response)
      loginSuccess({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        refreshTokenExpiresIn: data.refresh_token_expires_in,
      });

      // Clear any stale user cache from a previous session
      queryClient.removeQueries({ queryKey: AUTH_QUERY_KEYS.me });

      toast.success("Logged in successfully");
      navigate("/statistics");
    },

    onError: (
      error: AxiosError & {
        response: { data: { detail?: string; message?: string } };
      },
    ) => {
      toast.error(
        error.response?.data?.detail || "Login failed. Please try again.",
      );
    },
  });
}

// ─── useForgotPassword ─────────────────────────────────────────────────────────

/** Sends reset-password instructions to the submitted email address. */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (values: ForgotPasswordFormType) => forgotPasswordApi(values),

    onSuccess: (data) => {
      toast.success(
        data.detail ||
          data.message ||
          "Password reset instructions sent successfully",
      );
    },

    onError: (
      error: AxiosError<{ detail?: unknown; message?: unknown }>,
    ) => {
      const detail = error.response?.data?.detail;
      const message = error.response?.data?.message;

      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const messages = detail
          .map((item) => (item as { msg?: string })?.msg)
          .filter(Boolean)
          .join(", ");
        toast.error(
          messages || "Failed to send reset instructions. Please try again.",
        );
      } else if (typeof message === "string") {
        toast.error(message);
      } else {
        toast.error(
          error.message || "Failed to send reset instructions. Please try again.",
        );
      }
    },
  });
}

// ─── useResetPassword ──────────────────────────────────────────────────────────

/** Resets a user's password from the reset-password flow. */
export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (values: ResetPasswordPayload) => resetPasswordApi(values),

    onSuccess: (data) => {
      toast.success(
        data.detail || data.message || "Password reset successfully",
      );
      navigate("/users/sign_in");
    },

    onError: (
      error: AxiosError<{ detail?: unknown; message?: unknown }>,
    ) => {
      const detail = error.response?.data?.detail;
      const message = error.response?.data?.message;

      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const messages = detail
          .map((item) => (item as { msg?: string })?.msg)
          .filter(Boolean)
          .join(", ");
        toast.error(
          messages || "Failed to reset password. Please try again.",
        );
      } else if (typeof message === "string") {
        toast.error(message);
      } else {
        toast.error(
          error.message || "Failed to reset password. Please try again.",
        );
      }
    },
  });
}

// ─── useGetMe ─────────────────────────────────────────────────────────────────

/**
 * Fetches the current user profile from /auth/me.
 * Only runs when an access token exists.
 * React Query caches the result for 5 minutes; background refetches keep
 * it fresh on window focus and reconnect.
 */
export function useGetMe() {
  const { authToken } = useAuthStore();

  return useQuery<AuthUserProps, Error>({
    queryKey: AUTH_QUERY_KEYS.me,
    queryFn: getMeApi,
    enabled: !!authToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

// ─── useLogout ────────────────────────────────────────────────────────────────

/**
 * Calls POST /auth/logout on the server (best-effort) then always clears
 * local auth state so the user is logged out even if the API request fails.
 */
export function useLogout() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    // Order matters here, and getting it wrong loses data silently.
    //
    // The dashboard visit normally ends on DashboardPage's unmount — but that
    // unmount happens in onSettled below, AFTER logout() has cleared the access
    // token, so the final dashboard's `dashboard.dwell` was being queued with no
    // credential left to send it and dropped. Closing the visit here emits it
    // while the token is still valid, and flushing before the request means the
    // whole batch goes out authenticated.
    mutationFn: (refreshToken: string) => {
      endOpenVisit();
      telemetryQueue.flush();
      return logoutApi(refreshToken);
    },

    onSettled: () => {
      // Always clean up regardless of server response
      logout();
      usePortalAuthStore.getState().clearSession();
      queryClient.clear();
      toast.info("Logged out successfully");
      navigate("/users/sign_in");
    },
  });
}

// ─── useChangePassword ────────────────────────────────────────────────────────

/**
 * Changes the signed-in user's own password via POST /auth/change-password.
 *
 * confirmPassword is intentionally dropped here — it only exists so the
 * frontend can check the two new-password fields match (the `.refine` in
 * ChangePasswordSchema); the backend was told explicitly that match is a
 * frontend-only concern and its request body takes just current_password
 * + new_password.
 */
export function useChangePassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (values: ChangePasswordFormType) =>
      changePasswordApi({
        current_password: values.currentPassword,
        new_password: values.newPassword,
      }),

    onSuccess: (data) => {
      // Backend reuses `detail` for the success message too (confirmed:
      // 200 → {"detail": "Password updated successfully"}), so show it as-is
      // rather than a hard-coded string that could drift from the real copy.
      toast.success(data.detail);
      navigate("/dashboards");
    },

    onError: (error: AxiosError<{ detail?: unknown }>) => {
      const detail = error.response?.data?.detail;

      // 422 (malformed body / password too short) can return `detail` as an
      // array of validation objects instead of a string — the same FastAPI
      // quirk already handled in usePortal.ts's toastApiError. 400 (new
      // password same as current) and 401 (wrong current password, or an
      // expired/invalid bearer token — the spec overloads 401 for both) are
      // plain strings per the confirmed spec, but this guards either way.
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const messages = detail
          .map((item) => (item as { msg?: string })?.msg)
          .filter(Boolean)
          .join(", ");
        toast.error(
          messages || "Failed to update password. Please try again.",
        );
      } else {
        toast.error(
          error.message || "Failed to update password. Please try again.",
        );
      }
    },
  });
}
